import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import { useMessages, type MessageThread, type Message } from '../../hooks/useMessages';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import SaudiRiyalSymbol from '../../components/base/icons/SaudiRiyalSymbol';
import { formatAmount, latnLocale } from '../../utils/numberFormat';

function formatMessageTime(dateString: string, t: (key: string) => string): string {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const timeStr = date.toLocaleTimeString(latnLocale(), { hour: '2-digit', minute: '2-digit' });

  if (messageDate.getTime() === today.getTime()) {
    return `${t('enquiries:enquiries.chat.today')} ${timeStr}`;
  }
  if (messageDate.getTime() === yesterday.getTime()) {
    return `${t('enquiries:enquiries.chat.yesterday')} ${timeStr}`;
  }
  return `${date.toLocaleDateString(latnLocale())} ${timeStr}`;
}

function ThreadListItem({
  thread,
  isSelected,
  onClick,
  currentUserId,
  t,
  i18n,
}: {
  thread: MessageThread;
  isSelected: boolean;
  onClick: () => void;
  currentUserId?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
  i18n: { language: string };
}) {
  const otherParticipant = thread.participants.find((p) => p.id !== currentUserId);
  const displayName = otherParticipant?.fullName || otherParticipant?.firstName || otherParticipant?.email || 'Unknown';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 border-b border-gray-100 transition-colors hover:bg-gray-50 ${
        isSelected ? 'bg-[var(--pv-color-surface-sunken)] border-l-4 border-l-[#004D40]' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Property Image */}
        {thread.propertyImage ? (
          <img
            src={thread.propertyImage}
            alt={thread.propertyTitle || ''}
            className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-14 h-14 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
            <i className="ri-home-4-line text-xl text-gray-400"></i>
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Participant Name */}
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-gray-900 truncate">{displayName}</span>
            {thread.unreadCount > 0 && (
              <span className="flex-shrink-0 ml-2 px-2 py-0.5 text-xs font-medium text-white bg-[#004D40] rounded-full">
                {thread.unreadCount}
              </span>
            )}
          </div>

          {/* Property Title */}
          <p className="text-sm text-gray-600 truncate mb-1">{thread.propertyTitle || 'Property enquiry'}</p>

          {/* Last Message Preview */}
          {thread.lastMessage && (
            <p className="text-xs text-gray-500 truncate">
              {thread.lastMessage.senderId === currentUserId ? `${t('enquiries:enquiries.chat.you')}: ` : ''}
              {thread.lastMessage.content}
            </p>
          )}

          {/* Time */}
          {thread.lastMessage && (
            <p className="text-xs text-gray-400 mt-1">
              {new Date(thread.lastMessage.createdAt).toLocaleDateString(latnLocale(i18n.language === 'ar' ? 'ar-SA' : 'en-US'), {
                month: 'short',
                day: 'numeric',
              })}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

function ChatMessage({
  message,
  isOwn,
  t,
}: {
  message: Message;
  isOwn: boolean;
  t: (key: string) => string;
}) {
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
          isOwn
            ? 'bg-[#004D40] text-white rounded-br-md'
            : 'bg-gray-100 text-gray-900 rounded-bl-md'
        }`}
      >
        <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
        <p className={`text-xs mt-1 ${isOwn ? 'text-[#B7DDD7]' : 'text-gray-500'}`}>
          {formatMessageTime(message.createdAt, t)}
        </p>
      </div>
    </div>
  );
}

export default function EnquiriesPage() {
  const { t, i18n } = useTranslation(['enquiries', 'common']);
  const navigate = useNavigate();
  const { user, session, loading: authLoading } = useAuth();
  const { isSubscribed, loading: subscriptionLoading } = useSubscription();
  const {
    threads,
    currentThread,
    messages,
    loading: messagesLoading,
    error,
    fetchThreads,
    fetchThread,
    sendMessage,
    markAsRead,
    clearCurrentThread,
  } = useMessages();

  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Fetch threads on mount
  useEffect(() => {
    if (session?.token && isSubscribed) {
      fetchThreads();
    }
  }, [session?.token, isSubscribed, fetchThreads]);

  // Handle thread selection
  const handleSelectThread = useCallback(
    async (threadId: string) => {
      setSelectedThreadId(threadId);
      setMobileView('chat');
      await fetchThread(threadId);
      await markAsRead(threadId);
    },
    [fetchThread, markAsRead]
  );

  // Handle send message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedThreadId || !newMessage.trim() || sending) return;

    setSending(true);
    try {
      await sendMessage(selectedThreadId, newMessage);
      setNewMessage('');
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  // Handle back to list (mobile)
  const handleBackToList = () => {
    setMobileView('list');
    clearCurrentThread();
    setSelectedThreadId(null);
  };

  // Loading state
  if (authLoading || subscriptionLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-[#004D40] animate-spin mb-4"></i>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center max-w-md px-4">
            <i className="ri-lock-line text-6xl text-gray-400 mb-4"></i>
            <h2 className="text-xl font-bold text-[var(--pv-color-primary)] mb-2">
              {t('enquiries:enquiries.loginRequired.title')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('enquiries:enquiries.loginRequired.description')}
            </p>
            <Button onClick={() => navigate('/signin')}>
              <i className="ri-login-circle-line mr-2"></i>
              {t('enquiries:enquiries.loginRequired.loginButton')}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Not subscribed
  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center max-w-md px-4">
            <i className="ri-vip-crown-line text-6xl text-[#004D40] mb-4"></i>
            <h2 className="text-xl font-bold text-[var(--pv-color-primary)] mb-2">
              {t('enquiries:enquiries.accessDenied.title')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('enquiries:enquiries.accessDenied.description')}
            </p>
            <Button onClick={() => navigate('/membership')}>
              <i className="ri-shopping-bag-line mr-2"></i>
              {t('enquiries:enquiries.accessDenied.subscribeButton')}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[var(--pv-color-primary)]">{t('enquiries:enquiries.title')}</h1>
          <p className="text-gray-600">{t('enquiries:enquiries.subtitle')}</p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden" style={{ minHeight: '600px' }}>
          <div className="flex h-full" style={{ minHeight: '600px' }}>
            {/* Thread List (Left Panel) */}
            <div
              className={`w-full lg:w-1/3 border-r border-gray-200 flex flex-col ${
                mobileView === 'chat' ? 'hidden lg:flex' : 'flex'
              }`}
            >
              {/* List Header */}
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h2 className="font-semibold text-[var(--pv-color-primary)]">{t('enquiries:enquiries.threadList.title')}</h2>
              </div>

              {/* Thread List */}
              <div className="flex-1 overflow-y-auto">
                {messagesLoading && threads.length === 0 ? (
                  <div className="flex items-center justify-center h-40">
                    <i className="ri-loader-4-line text-2xl text-[#004D40] animate-spin"></i>
                  </div>
                ) : threads.length === 0 ? (
                  <div className="p-6 text-center">
                    <i className="ri-chat-3-line text-5xl text-gray-300 mb-3"></i>
                    <p className="text-gray-600 font-medium">{t('enquiries:enquiries.noThreads')}</p>
                    <p className="text-sm text-gray-500 mt-1">{t('enquiries:enquiries.noThreadsDescription')}</p>
                  </div>
                ) : (
                  threads.map((thread) => (
                    <ThreadListItem
                      key={thread.id}
                      thread={thread}
                      isSelected={selectedThreadId === thread.id}
                      onClick={() => handleSelectThread(thread.id)}
                      currentUserId={user?.id}
                      t={t}
                      i18n={i18n}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Chat View (Right Panel) */}
            <div
              className={`w-full lg:w-2/3 flex flex-col ${
                mobileView === 'list' ? 'hidden lg:flex' : 'flex'
              }`}
            >
              {currentThread ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-gray-200 bg-white flex items-center gap-3">
                    {/* Back Button (Mobile) */}
                    <button
                      type="button"
                      onClick={handleBackToList}
                      className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100"
                    >
                      <i className="ri-arrow-left-line text-xl"></i>
                    </button>

                    {/* Property Info */}
                    {currentThread.propertyImage && (
                      <img
                        src={currentThread.propertyImage}
                        alt={currentThread.propertyTitle || ''}
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {currentThread.propertyTitle || 'Property Enquiry'}
                      </h3>
                      {currentThread.propertyPrice && (
                        <p className="text-sm text-[#004D40] flex items-center gap-1">
                          <SaudiRiyalSymbol className="h-3 w-3" />
                          {formatAmount(currentThread.propertyPrice)}
                        </p>
                      )}
                    </div>

                    {/* View Property Link */}
                    {currentThread.propertyId && (
                      <Link
                        to={`/property/${currentThread.propertyId}`}
                        className="flex-shrink-0 px-3 py-1.5 text-sm font-medium text-[#004D40] bg-[var(--pv-color-surface-sunken)] rounded-lg hover:bg-[#DDEEEB] transition-colors"
                      >
                        {t('enquiries:enquiries.property.viewProperty')}
                      </Link>
                    )}
                  </div>

                  {/* Messages Container */}
                  <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 bg-gray-50">
                    {messagesLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <i className="ri-loader-4-line text-2xl text-[#004D40] animate-spin mb-2"></i>
                          <p className="text-sm text-gray-500">{t('enquiries:enquiries.chat.loadingMessages')}</p>
                        </div>
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <i className="ri-chat-new-line text-4xl text-gray-300 mb-2"></i>
                          <p className="text-gray-500">{t('enquiries:enquiries.chat.noMessages')}</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        {messages.map((message) => (
                          <ChatMessage
                            key={message.id}
                            message={message}
                            isOwn={message.senderId === user?.id}
                            t={t}
                          />
                        ))}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>

                  {/* Message Input */}
                  <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white">
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={t('enquiries:enquiries.chat.placeholder')}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#004D40] focus:border-transparent"
                        disabled={sending}
                      />
                      <button
                        type="submit"
                        disabled={!newMessage.trim() || sending}
                        className="px-6 py-3 bg-[#004D40] text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sending ? (
                          <i className="ri-loader-4-line animate-spin"></i>
                        ) : (
                          <i className="ri-send-plane-fill"></i>
                        )}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                /* No Thread Selected */
                <div className="flex-1 flex items-center justify-center bg-gray-50">
                  <div className="text-center">
                    <i className="ri-chat-smile-3-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500">{t('enquiries:enquiries.chat.startConversation')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <i className="ri-error-warning-line mr-2"></i>
            {error}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
